import { LazyQueryExecFunction } from '@apollo/client/react';

import {
  Asset,
  AssetFilterInputObject,
  AssetsSearchListQuery,
  AssetsSearchListQueryVariables,
  FilterGroupingEnum,
  SortDirectionEnum,
} from 'graphql-common';
import { DEFAULT_SEARCH_LIST_VARIABLES } from '@lib/enums/urls';
import getLoadOptionsResult from '@lib/utils/getLoadOptionsResult';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { LoadOptionsParams } from '@lib/components/Select/types';

type FetchLazyQuery = LazyQueryExecFunction<
  AssetsSearchListQuery,
  AssetsSearchListQueryVariables
>;

export const fetchSearchList = async (
  searchQuery: string,
  fetchLazyQuery: FetchLazyQuery,
  page: number,
  selectedSiteId?: string,
  selectedSiteAreaId?: string,
  additionalFilters?: object[],
) => {
  const options: { variables: AssetsSearchListQueryVariables } = {
    variables: {
      ...DEFAULT_SEARCH_LIST_VARIABLES,
      page,
      sorts: { name: SortDirectionEnum.Asc },
    },
  };
  const filters: AssetFilterInputObject[] = [];
  if (additionalFilters?.length) {
    filters.push(...(additionalFilters as AssetFilterInputObject[]));
  }
  if (selectedSiteId) {
    filters.push({
      siteId: {
        grouping: FilterGroupingEnum.And,
        predicate: { eq: selectedSiteId },
        filterGroup: 'siteId',
      },
    });
  }
  if (selectedSiteAreaId) {
    filters.push(
      ...[
        {
          siteAreaId: {
            grouping: FilterGroupingEnum.Or,
            predicate: { eq: selectedSiteAreaId },
            filterGroup: 'siteAreaIdOrNull',
          },
        },
        {
          siteAreaId: {
            grouping: FilterGroupingEnum.Or,
            predicate: { null: true },
            filterGroup: 'siteAreaIdOrNull',
          },
        },
      ],
    );
  }
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'name' },
        { fieldName: 'code' },
      ]),
    );
  }
  if (filters.length) {
    options.variables.filters = filters;
  }
  const { data } = await fetchLazyQuery(options);
  return getLoadOptionsResult(
    data,
    (dataItem: Asset) => ({
      value: dataItem.id,
      label: `${dataItem.fullCode} ${dataItem.name}`,
      data: dataItem,
    }),
    searchQuery,
  );
};

export const getAssetsLoadOptions =
  (
    fetchLazyQuery: FetchLazyQuery,
    selectedSiteId?: string,
    selectedSiteAreaId?: string,
    additionalFilters?: object[],
  ) =>
  (searchQuery: string, loadedOptions: unknown, params: LoadOptionsParams) =>
    fetchSearchList(
      searchQuery,
      fetchLazyQuery,
      params?.page || 1,
      selectedSiteId,
      selectedSiteAreaId,
      additionalFilters,
    );
